import { useRef, useEffect, useCallback } from 'react';
// Shop-floor diagnostics (migration 047). See ../diagnostics.
import { logScannerDiagnostic } from '../diagnostics';

/**
 * Hook for capturing hardware barcode scanner input on Android handhelds.
 *
 * THE SHOP FLOOR DEVICE IS IN HID MODE. The Svantto MC002's scan-settings app
 * (iScanPlus > Application Setting > Scan result sending mode) offers focus,
 * broadcast, HID, copy and combinations. It is set to **HID**, which delivers
 * real key events, so the document-level listener below is the live path and no
 * focused element is required.
 *
 * Set exactly one delivery mode on the device. The combined modes (`HID &
 * Broadcast`, `focus & Broadcast`) would deliver every scan twice if a second
 * listener is ever added.
 *
 * Three capture paths are kept, because the mode is a device setting a factory
 * reset can undo and other scanners behave differently:
 * 1. `document` keydown — real key events (HID). THE LIVE PATH.
 * 2. `input` event — InputConnection batch delivery, for a device in focus mode
 * 3. `keydown` Enter on the hidden input — char-by-char wedge into a focused field
 *
 * ⚠ DO NOT ADD inputMode="none" TO THE HIDDEN INPUT. It was tried, and on these
 * handhelds it suppresses InputConnection entirely: only the trailing Enter
 * arrives, with no character data, so scans fail silently in focus mode. See the
 * note in ScannerMode.js.
 *
 * Both focus behaviors below yield to a field the operator deliberately tapped.
 * The hook used to steal focus back unconditionally and dismiss any keyboard it
 * saw appear, which fought the manual serial entry field mid-typing.
 */
// Tags that mean "the operator is typing here". Used by both focus behaviors to
// tell a field someone chose from the hook's own off-screen input.
const OPERATOR_FIELD_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

/**
 * HOW THE FLEET'S SCANNERS DELIVER. One knob, because it must match a setting on
 * the physical devices and drifting per-call-site would be untraceable.
 *
 *   'document'  real key events, read at document level. NOTHING IS FOCUSED, so
 *               Android never raises the soft keyboard. Requires the device on
 *               HID output.
 *   'input'     the hidden input is focused and holds the caret, which is what
 *               InputConnection and keyboard-wedge delivery require.
 *
 * ⚠ THESE ARE THE ONLY TWO OPTIONS, AND YOU CANNOT HAVE BOTH. A focused text
 * input on Android raises the keyboard, and every way to suppress it —
 * inputMode="none", readOnly — also suppresses IME delivery, which is the bug
 * that cost three debug cycles. So keyboard-free and InputConnection are
 * mutually exclusive, and the device's output mode picks which one you get.
 *
 * The fleet is on HID (iScanPlus > Application Setting > Scan result sending
 * mode). If a device is ever reset to focus/copy output, flip this to 'input'
 * and accept a keyboard on the badge screen. `scanner_diagnostics` tells you
 * which has happened: no rows at all means the device is not delivering at
 * document level.
 */
const DEVICE_CAPTURE_MODE = 'document';

const useScannerInput = ({
  onScan,
  // Which workflow this hook serves, for the diagnostics log (047).
  surface = 'unknown',
  minLength = 12,
  validator = (val) =>
    /^[A-Fa-f0-9]{12}$/i.test(val) ||
    /^[A-Fa-f0-9]{16}$/i.test(val) ||
    /^[A-Fa-f0-9]{24}$/i.test(val) ||
    // "<station_type>:<uid>" board sticker. station_type is fireflasher's
    // StationType enum (src/hardware/base.py) — AP, AP_UBLOX, AP_MULTI, ESC,
    // BAT, CHG, AUTO, plus whatever jig lands next — NOT boards.board_type.
    // Enumerating it here once meant AP_UBLOX wedge scans were dropped before
    // the workflow ever saw them.
    /^[A-Za-z][A-Za-z0-9_-]*:[A-Fa-f0-9]{12,24}$/.test(val) ||
    /^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/i.test(val),
  disabled = false,
}) => {
  const inputRef = useRef(null);
  const lastScanRef = useRef(null);
  const processingRef = useRef(false);

  const processScan = useCallback((value, path) => {
    if (processingRef.current) return;

    if (value.length >= minLength && validator(value)) {
      processingRef.current = true;
      lastScanRef.current = value;
      // Diagnostics (047). `path` is the whole point: it says which of
      // the three delivery mechanisms actually fired, which is not otherwise
      // observable on a device with no devtools.
      logScannerDiagnostic({
        surface, event: 'captured', raw_value: value, detail: { path, length: value.length },
      });
      onScan(value);
      // Prevent double-processing from overlapping event types
      setTimeout(() => { processingRef.current = false; }, 200);
      return;
    }

    // The silent drop. Everything upstream looks identical to a dead scanner
    // from the operator's side, so this is the single most useful line here.
    if (value.length > 0) {
      logScannerDiagnostic({
        surface,
        event: 'rejected',
        raw_value: value,
        detail: { path, length: value.length, min_length: minLength },
      });
    }
  }, [onScan, minLength, validator, surface]);

  // Focus the hidden input. A no-op in document mode, where focusing is exactly
  // what raises the keyboard we do not want.
  const focusInput = useCallback(() => {
    if (DEVICE_CAPTURE_MODE !== 'input') return;
    if (inputRef.current && !disabled) {
      inputRef.current.focus({ preventScroll: true });
    }
  }, [disabled]);

  // Auto-focus on mount and re-focus on blur
  useEffect(() => {
    if (disabled || DEVICE_CAPTURE_MODE !== 'input') return;
    focusInput();

    const input = inputRef.current;
    if (!input) return;

    const handleBlur = () => {
      // Delay allows button taps to register before re-stealing focus.
      //
      // Re-check what has focus WHEN THE TIMER FIRES, not now. If the operator
      // tapped a real field in the meantime — the manual drone-serial entry is on
      // screen throughout NEEDS_SERIAL — stealing focus back yanks the caret away
      // mid-typing. That is half of the keyboard-versus-scanner contention.
      setTimeout(() => {
        const active = document.activeElement;
        if (active && active !== input && OPERATOR_FIELD_TAGS.has(active.tagName)) return;
        focusInput();
      }, 300);
    };

    input.addEventListener('blur', handleBlur);
    return () => input.removeEventListener('blur', handleBlur);
  }, [focusInput, disabled]);

  // Input event listener — captures InputConnection (Android IME) delivery
  useEffect(() => {
    const input = inputRef.current;
    if (!input || disabled) return;

    const handleInput = () => {
      const value = input.value.trim();
      // Scanner may dump entire barcode in one InputConnection commit
      if (value.length >= minLength && validator(value)) {
        processScan(value, 'input-event');
        input.value = '';
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const value = input.value.trim();
        processScan(value, 'input-enter');
        input.value = '';
      }
    };

    input.addEventListener('input', handleInput);
    input.addEventListener('keydown', handleKeyDown);
    return () => {
      input.removeEventListener('input', handleInput);
      input.removeEventListener('keydown', handleKeyDown);
    };
  }, [processScan, minLength, validator, disabled]);

  // Fallback: document-level keydown for USB HID scanners (real key events)
  useEffect(() => {
    if (disabled) return;

    let buffer = '';
    let lastKey = 0;
    let clearTimer = null;

    const handleKeyDown = (e) => {
      // Skip if the input element already handled this
      if (e.target === inputRef.current) return;
      // Skip if user is typing in a real form field
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      const now = Date.now();
      if (now - lastKey > 80) buffer = '';
      lastKey = now;

      if (e.key === 'Enter') {
        e.preventDefault();
        processScan(buffer.trim(), 'document-keydown');
        buffer = '';
        return;
      }

      if (e.key.length === 1) {
        buffer += e.key;
        clearTimeout(clearTimer);
        clearTimer = setTimeout(() => { buffer = ''; }, 500);
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      clearTimeout(clearTimer);
    };
  }, [processScan, disabled]);

  // PWA keyboard suppression: detect keyboard via visualViewport shrinkage
  useEffect(() => {
    if (disabled || DEVICE_CAPTURE_MODE !== 'input') return;
    const vv = window.visualViewport;
    if (!vv) return;

    let baseHeight = vv.height;
    let dismissing = false;

    const handleResize = () => {
      if (dismissing) return;

      // Only OUR keyboard is ours to dismiss.
      //
      // This handler exists to close a soft keyboard that appeared over the
      // hidden input, which the operator never asked for. When a real field has
      // focus the keyboard is WANTED, and closing it made manual serial entry
      // impossible: type a character, the viewport shrinks, this fired, the
      // keyboard shut. Re-baseline and leave it alone.
      if (document.activeElement !== inputRef.current) {
        baseHeight = Math.max(baseHeight, vv.height);
        return;
      }

      // Keyboard likely appeared if viewport shrank by >15%
      if (vv.height < baseHeight * 0.85) {
        dismissing = true;
        // Blur to dismiss keyboard
        if (document.activeElement === inputRef.current) {
          inputRef.current.blur();
        }
        // After keyboard dismisses, refocus for next scan
        setTimeout(() => {
          baseHeight = vv.height; // Update baseline
          dismissing = false;
          focusInput();
        }, 400);
      } else if (vv.height > baseHeight) {
        // Viewport grew (keyboard dismissed or orientation change)
        baseHeight = vv.height;
      }
    };

    vv.addEventListener('resize', handleResize);
    return () => vv.removeEventListener('resize', handleResize);
  }, [disabled, focusInput]);

  const clearScan = useCallback(() => {
    lastScanRef.current = null;
    if (inputRef.current) inputRef.current.value = '';
  }, []);

  return {
    inputRef,
    lastScan: lastScanRef.current,
    clearScan,
    focusInput,
  };
};

export default useScannerInput;
